import "./dashboard.scss";
import { useEffect, useState } from "react";
import { SectionHeader } from "@/sprout/typography";
import { usePage } from "@inertiajs/react";

import ProfileCard from "@components/profile_card";
import RecentlyViewedJobs from "@/components/dashboard/widgets/recently_viewed_jobs_widget";
import JobAlerts from "@/components/dashboard/widgets/job_alerts/widget";
import Tasks from "@/components/dashboard/widgets/tasks_widget";
import Applications from "@/components/dashboard/widgets/applications_widget";
import SearchSection from "@/components/dashboard/search_section.tsx";
import DashboardUploadResumeModal from "@components/dashboard/dashboard_upload_resume_modal";
import { DashboardPreviewProvider } from "@components/dashboard/preview_context";
import { type RecentSearch } from "@/types/job_search/recent_searches";

import useDevice from "@/hooks/use_device";
import useTranslation from "@/hooks/use_translation";
import useProfile from "@/queries/use_profile";
import Fingerprint15 from "../images/fingerprint_15.svg";
import { SurveyCallout } from "@components/survey/survey_callout";
import DreamJobBanner, { type DreamJobBannerState } from "@components/dashboard/dream_job_banner";
import { useEffectOnce } from "react-use";
import { Option } from "@sprout/select";
import JobSearchWidget from "@components/dashboard/widgets/job_search_widget.tsx";
import JobRecommendationsSection from "@components/dashboard/job_recommendations_section.tsx";

export type Subscription = {
  boardName: string;
  filters: string[];
  path: string;
  editPath: string;
  logoUrl: string;
  updatedAt: Date;
};

type PageProps = {
  uploadResumeModal?: boolean;
  profileWidgetEnhancement?: boolean;
};

type Props = {
  firstName?: string;
  paths: {
    notificationSchedulePath: string;
    recentlyViewedJobsPath: string;
    quickApplyPath: string;
    workPreferencesPath: string;
  };
  closeOnLoad?: boolean;
  recentlyViewedJobsPerPageDashboard: number;
  newUser: boolean;
  landedDreamJob?: boolean;
  preview?: boolean;
  email?: string;
  employmentTypes: Option[];
  worksites: Option[];
  recentSearches: RecentSearch[];
  dreamJobBanner?: { state: DreamJobBannerState; count: number };
  jobRecommendationsEnabled: boolean;
  jobSeekersTasksEnabled: boolean;
};

const MobileDashboard = ({
  paths,
  recentlyViewedJobsPerPageDashboard,
  employmentTypes,
  worksites,
  recentSearches,
  jobRecommendationsEnabled,
  jobSeekersTasksEnabled,
}: Props) => {
  const { profileWidgetEnhancement } = usePage<PageProps>().props;
  return (
    <>
      <JobSearchWidget recentSearches={recentSearches} />
      {profileWidgetEnhancement && (
        <ProfileCard
          quickApplyPath={paths.quickApplyPath}
          workPreferencesPath={paths.workPreferencesPath}
          employmentTypes={employmentTypes}
          worksites={worksites}
        />
      )}
      {jobRecommendationsEnabled && <JobRecommendationsSection />}
      <Applications />
      {!jobSeekersTasksEnabled && <Tasks />}
      <RecentlyViewedJobs
        path={paths.recentlyViewedJobsPath}
        perPage={recentlyViewedJobsPerPageDashboard}
      />
      <JobAlerts />
    </>
  );
};

const DesktopDashboard = ({
  paths,
  recentlyViewedJobsPerPageDashboard,
  employmentTypes,
  worksites,
  recentSearches,
  jobRecommendationsEnabled,
  jobSeekersTasksEnabled,
}: Props) => {
  return (
    <>
      <div className="dashboard__col1">
        <JobSearchWidget recentSearches={recentSearches} />
        {jobRecommendationsEnabled && <JobRecommendationsSection />}
        <Applications />
        {!jobSeekersTasksEnabled && <Tasks />}
      </div>
      <div className="dashboard__col2">
        <ProfileCard
          quickApplyPath={paths.quickApplyPath}
          workPreferencesPath={paths.workPreferencesPath}
          employmentTypes={employmentTypes}
          worksites={worksites}
        />
        <RecentlyViewedJobs
          path={paths.recentlyViewedJobsPath}
          perPage={recentlyViewedJobsPerPageDashboard}
        />
        <JobAlerts />
      </div>
    </>
  );
};

const Dashboard = (props: Props) => {
  const {
    firstName,
    landedDreamJob,
    newUser,
    closeOnLoad,
    preview,
    email,
    recentSearches,
    dreamJobBanner,
  } = props;

  const { t } = useTranslation("dashboard");

  const device = useDevice();
  const { uploadResumeModal, profileWidgetEnhancement } = usePage<PageProps>().props;
  const { data: profile } = useProfile({ mock: preview });
  const [showUploadResumeModal, setShowUploadResumeModal] = useState(false);

  useEffect(() => {
    if (!uploadResumeModal || profile?.resume || localStorage.getItem("uploadResumeModalShown")) {
      return;
    }

    const timer = setTimeout(() => {
      localStorage.setItem("uploadResumeModalShown", "true");
      setShowUploadResumeModal(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [uploadResumeModal, profile?.resume]);

  useEffectOnce(() => {
    if (closeOnLoad) {
      window.close();
    }
  });

  const dashboardTitle = () => {
    if (newUser) {
      return t("header.title_new_user");
    }

    return firstName ? t("header.title_with_name", { name: firstName }) : t("header.title");
  };

  const dashboardSubtitle = () => {
    if (landedDreamJob) {
      return t("header.subtitle_dream_job");
    }

    if (newUser) {
      return t("header.subtitle_new_user");
    }

    const subtitles = t("header.subtitle", { returnObjects: true }) as string[];
    return subtitles[Math.floor(Math.random() * subtitles.length)];
  };

  const content = (
    <>
      {dreamJobBanner ? (
        <DreamJobBanner state={dreamJobBanner.state} count={dreamJobBanner.count} />
      ) : (
        <div className="welcome-banner with-search">
          <div className="welcome-container">
            <div className="welcome-text-wrapper">
              <div className="welcome-text">
                <SectionHeader serif large as="h1" className="welcome-title">
                  {dashboardTitle()}
                </SectionHeader>
                <SectionHeader className="welcome-subtitle">{dashboardSubtitle()}</SectionHeader>
              </div>
            </div>
            <SearchSection recentSearches={recentSearches} />
            <img src={Fingerprint15} alt="Welcome Banner Image" className="welcome-banner__image" />
          </div>
          <img src={Fingerprint15} alt="Welcome Banner Image" className="welcome-banner__image" />
        </div>
      )}
      <div className="dashboard">
        <SurveyCallout />
        <main>
          {device.isMobile ? <MobileDashboard {...props} /> : <DesktopDashboard {...props} />}
        </main>
      </div>
      <DashboardUploadResumeModal
        open={showUploadResumeModal}
        onOpenChange={setShowUploadResumeModal}
        profileWidgetEnhancementEnabled={profileWidgetEnhancement}
      />
    </>
  );

  if (preview) {
    return <DashboardPreviewProvider email={email ?? ""}>{content}</DashboardPreviewProvider>;
  }

  return content;
};

export default Dashboard;
